'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRouter } from 'next/navigation';
import { ArrowRight, Check, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { signIn } from '@/lib/auth/client';
import { describeSignInError, type SignInErrorDescription } from '@/lib/auth/auth-error';

const Schema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email'),
  password: z.string().min(1, 'Password is required'),
});
type FormValues = z.infer<typeof Schema>;

export interface LoginFormProps {
  /** Post-login landing path (the scope ladder runs server-side from there). */
  nextPath: string;
  /** Accent color (CSS var) for the loading spinner — matches the variant tone. */
  tone?: string;
  /**
   * Origin the auth server trusts, supplied by the server component from the
   * runtime `BETTER_AUTH_URL`. Falls back to the build-time public var, which is
   * empty in deployed images — see `lib/auth/canonical-origin.ts`.
   */
  canonicalOrigin?: string;
}

/**
 * Credential form — design handoff `auth.jsx` (merchant variant), wired to the
 * real Better Auth `signIn.email`. Validation stays on react-hook-form + zod
 * (the prototype only disabled the button), so empty/invalid input surfaces a
 * clear inline message instead of a silently dead button.
 */
export function LoginForm({
  nextPath,
  tone = 'var(--cyan)',
  canonicalOrigin = process.env.NEXT_PUBLIC_BETTER_AUTH_URL ?? '',
}: LoginFormProps) {
  const router = useRouter();
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [submitError, setSubmitError] = useState<SignInErrorDescription | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(Schema) });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    const result = await signIn.email({
      email: values.email,
      password: values.password,
      rememberMe: remember,
    });
    if (result.error || !result.data) {
      // Never collapse every failure into "wrong password" — a rejected origin,
      // a shared-IP rate limit and a 500 are not the user's credentials, and
      // saying so sends them chasing a password that was never checked.
      setSubmitError(
        describeSignInError(result.error ?? {}, {
          currentOrigin: window.location.origin,
          canonicalOrigin,
          currentPath: window.location.pathname + window.location.search,
        }),
      );
      return;
    }
    router.push(nextPath);
  });

  return (
    <form className="stack" style={{ gap: 18 }} onSubmit={onSubmit} noValidate>
      <div className="stack" style={{ gap: 7 }}>
        <label htmlFor="login-email" className="muted" style={{ fontSize: 13 }}>
          Email
        </label>
        <input
          id="login-email"
          className="enr-input"
          type="email"
          autoComplete="username"
          placeholder="you@company.com"
          {...register('email')}
        />
        {errors.email && (
          <span style={{ fontSize: 12.5, color: 'var(--rose)' }}>{errors.email.message}</span>
        )}
      </div>

      <div className="stack" style={{ gap: 7 }}>
        <div className="between">
          <label htmlFor="login-password" className="muted" style={{ fontSize: 13 }}>
            Password
          </label>
          <a href="/forgot-password" className="link-arrow" style={{ fontSize: 12.5 }}>
            Forgot?
          </a>
        </div>
        <div style={{ position: 'relative' }}>
          <input
            id="login-password"
            className="enr-input"
            type={showPw ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            style={{ paddingRight: 44 }}
            {...register('password')}
          />
          <button
            type="button"
            onClick={() => setShowPw((s) => !s)}
            aria-label={showPw ? 'Hide' : 'Reveal'}
            title={showPw ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              color: 'var(--fg-subtle)',
              padding: 8,
              cursor: 'pointer',
              display: 'inline-flex',
            }}
          >
            {showPw ? <Eye size={17} /> : <EyeOff size={17} />}
          </button>
        </div>
        {errors.password && (
          <span style={{ fontSize: 12.5, color: 'var(--rose)' }}>{errors.password.message}</span>
        )}
      </div>

      <div className="between">
        <label
          className="row"
          style={{ gap: 8, cursor: 'pointer', fontSize: 13.5, color: 'var(--fg-soft)' }}
        >
          <span
            className="auth-check"
            data-on={remember}
            onClick={() => setRemember((r) => !r)}
            role="checkbox"
            aria-checked={remember}
            aria-label="Keep me signed in"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                setRemember((r) => !r);
              }
            }}
          >
            {remember && <Check size={13} />}
          </span>
          Keep me signed in
        </label>
      </div>

      {submitError && (
        <span role="alert" style={{ fontSize: 13, color: 'var(--rose)' }}>
          {submitError.message}
          {submitError.canonicalUrl && (
            <>
              {' '}
              <a href={submitError.canonicalUrl} className="link-arrow">
                Go to the correct address
              </a>
            </>
          )}
        </span>
      )}

      <button type="submit" className="btn btn-primary btn-lg" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <RefreshCw size={17} className="fd-spin" style={{ color: tone }} />
            Signing in…
          </>
        ) : (
          <>
            Sign in
            <ArrowRight size={17} />
          </>
        )}
      </button>
    </form>
  );
}
